/*
 * Simplified and unified sidebar-item CSS:
 * - Unified badge overlay logic
 * - Grouped collapsed mode rules
 * - Combined state rules (active, disabled, hover, focus)
 * - Clarified selectors for maintainability
 */

@layer refine.base {
  :host {
    display: block;
    min-width: 0;
    max-width: 100%;
  }

  .item {
    position: relative;
    box-sizing: border-box;
    display: flex;
    gap: var(--size-2);
    align-items: center;
    min-width: 0;
    min-height: var(--_touch-target);
    padding: var(--size-1-5) var(--size-2);
    font-size: var(--sidebar-item-font-size, var(--text-sm));
    font-weight: var(--font-medium);
    color: var(--sidebar-item-color, var(--color-contrast-700));
    text-decoration: none;
    cursor: pointer;
    user-select: none;
    outline: none;
    border-radius: var(--rounded-lg);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  a.item {
    text-decoration: none;
  }

  .item:hover,
  .item:focus-visible {
    color: var(--sidebar-item-hover-color, var(--color-contrast-900));
    outline: var(--focus-ring);
    outline-offset: 1px;
    background: var(--sidebar-item-hover-bg, var(--color-contrast-100));
  }

  :host([active]) .item {
    font-weight: var(--font-semibold);
    color: var(--sidebar-item-active-color, var(--color-primary));
    background: var(
      --sidebar-item-active-bg,
      var(--color-primary-100, color-mix(in oklch, var(--color-primary) 12%, transparent))
    );
  }

  :host([active]) .item::before {
    display: none;
  }

  :host([disabled]) .item,
  :host([disabled]) .item:hover,
  :host([disabled]) .item:focus-visible {
    color: var(--sidebar-item-disabled-color, var(--color-contrast-300));
    pointer-events: none;
    cursor: not-allowed;
    outline: none;
    background: none;
    opacity: 1;
  }

  :host([disabled]) .item-icon {
    color: var(--sidebar-item-disabled-color, var(--color-contrast-300));
  }

  .item-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    height: var(--size-5);
    margin: 0 auto;
  }

  .item-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: opacity var(--transition-fast);
  }

  .item-end {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-1);
    align-items: center;
    transition: opacity var(--transition-fast);
  }

  /* Collapsed sidebar: hide label text, adjust layout, overlay badge */
  :host([sidebar-collapsed]) .item,
  :host([sidebar-collapsed]) .item-icon {
    position: relative;
  }

  :host([sidebar-collapsed]) .item-label {
    width: 0;
    overflow: hidden;
    pointer-events: none;
    opacity: 0;
  }

  :host([sidebar-collapsed]) .item {
    gap: 0;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 100%;
    padding: 0;
  }

  :host([sidebar-collapsed]) .item::before {
    inset-block: var(--size-2);
  }

  :host([sidebar-collapsed]) .item-icon {
    width: var(--size-7);
    height: var(--size-7);
    padding: var(--size-1-5);
    font-size: 1.5em;
  }

  :host([sidebar-collapsed][active]) .item {
    background: none;
  }

  :host([sidebar-collapsed]) .item:hover {
    background: none;
  }

  :host([sidebar-collapsed][active]) .item-icon {
    background: var(
      --sidebar-item-active-bg,
      var(--color-primary-100, color-mix(in oklch, var(--color-primary) 12%, transparent))
    );
    border-radius: var(--rounded-full);
  }

  :host([sidebar-collapsed]) .item-icon:hover {
    background: var(--color-contrast-100);
    border-radius: var(--rounded-full);
  }

  /* Unified badge overlay logic for collapsed mode */
  :host([sidebar-collapsed]) .item-end,
  :host([sidebar-collapsed]) .item-icon .group-badge,
  :host([sidebar-collapsed]) .item-icon ::slotted([slot='end']) {
    position: absolute;
    right: 0;
    bottom: calc(var(--size-2) * -1);
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25em;
    min-height: 1.25em;
    pointer-events: auto;
    opacity: 1;
    transform: translate(40%, -40%);
  }

  :host([dir='rtl'][sidebar-collapsed]) .item-end,
  :host([dir='rtl'][sidebar-collapsed]) .item-icon .group-badge,
  :host([dir='rtl'][sidebar-collapsed]) .item-icon ::slotted([slot='end']),
  :host(:dir(rtl)[sidebar-collapsed]) .item-end,
  :host(:dir(rtl)[sidebar-collapsed]) .item-icon .group-badge,
  :host(:dir(rtl)[sidebar-collapsed]) .item-icon ::slotted([slot='end']) {
    right: auto;
    left: 0;
    transform: translate(-40%, -40%);
  }

  :host([sidebar-collapsed]) .item-end .group-badge,
  :host([sidebar-collapsed]) .item-end ::slotted([slot='end']) {
    position: absolute;
    top: 50%;
    right: calc(var(--size-0-5) * -1);
    z-index: 1;
    transform: translateY(-50%);
  }

  :host([dir='rtl'][sidebar-collapsed]) .item-end .group-badge,
  :host([dir='rtl'][sidebar-collapsed]) .item-end ::slotted([slot='end']),
  :host(:dir(rtl)[sidebar-collapsed]) .item-end .group-badge,
  :host(:dir(rtl)[sidebar-collapsed]) .item-end ::slotted([slot='end']) {
    right: auto;
    left: var(--size-2);
  }

  /* The bottom tab bar (`.bottom-bar` in sidebar.ts) renders its own promoted-item
     buttons from the same data — the original <ore-sidebar-item> stays inside the
     (visually off-screen while closed) <nav>, so it must be fully hidden here to
     avoid a focusable/AT-visible duplicate of the bottom-bar entry. */
  :host([sidebar-bottom-nav]) {
    display: none;
  }
}
